import { useAtomValue } from "jotai";
import classNames from "classnames";
import {
  sideBarSelectedAtom,
  SUMMARY,
  sidebarAnimationAtom,
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
  isBlankCanvasAtom,
} from "@zapier/canvas-state";
import { DEFAULT_SIDEBAR_WIDTH } from "@zapier/canvas-constants";
import { CopilotChatPanel } from "@zapier/ai-toolkit/client";
import { IconButton } from "@zapier/design-system";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { useCopilotDrawerState } from "@zapier/canvas-state";
import { SummaryPanel } from "../SummaryPanel";
import DevTools from "../DevTools/DevTools";
import { getCopilotSuggestions } from "@zapier/copilot-tools";
import styles from "./SidebarDrawer.module.css";

type SidebarDrawerProps = {
  onCloseClick: () => void;
};

export default function SidebarDrawer({ onCloseClick }: SidebarDrawerProps) {
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const [isCopilotOpen] = useCopilotDrawerState();
  const sidebarAnimation = useAtomValue(sidebarAnimationAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const nodes = useAtomValue(nodesAtom);
  const isBlankCanvas = useAtomValue(isBlankCanvasAtom);

  // Get suggestions based on canvas state using proper blank canvas logic
  const copilotSuggestions = getCopilotSuggestions({
    isBlankCanvas,
    isDraftModeIncludingTemplates,
    nodes,
  });

  const SIDEBAR_CONTENT = {
    [SUMMARY]: {
      title: capitalizeFirstLetter(SUMMARY),
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>{capitalizeFirstLetter(SUMMARY)}</div>
            <IconButton
              name="arrowSmallLeft"
              variant="tertiary"
              size="small"
              onPress={onCloseClick}
            />
          </div>
          <div className={styles.componentContainer}>
            <SummaryPanel />
          </div>
        </>
      ),
    },
    devTools: {
      title: "🔒 DevTools (⌘D)",
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>🔒 DevTools (⌘D)</div>
            <div className={styles.closeButtonWrapper}>
              <IconButton
                name="arrowSmallLeft"
                variant="tertiary"
                size="small"
                onPress={onCloseClick}
              />
            </div>
          </div>
          <div
            className={classNames(styles.componentContainer, styles.withScroll)}
          >
            <DevTools />
          </div>
        </>
      ),
    },
    copilot: {
      title: "Copilot",
      component: (
        <div
          className={classNames(styles.copilotChatPanelContainer, {
            [styles.fadeIn]: sidebarAnimation === "fadeIn",
          })}
        >
          <CopilotChatPanel
            placeholder="Ask me anything about your canvas..."
            onCloseClick={onCloseClick}
            // @ts-ignore - Type mismatch due to zinnia-icons version incompatibility:
            // ai-toolkit uses an older version of zinnia-icons than canvas, causing TypeScript
            // errors when newer icons (like navAssetsFill) are used in suggestions.
            emptyStateSuggestions={copilotSuggestions}
          />
        </div>
      ),
    },
  };

  const selectedSidebar = (() => {
    // If copilot is open, show copilot sidebar
    if (isCopilotOpen) {
      return SIDEBAR_CONTENT.copilot;
    }

    // Otherwise, show the selected sidebar if it exists
    if (
      sideBarSelected &&
      SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
    ) {
      return SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT];
    }

    // Default fallback
    return {
      title: "",
      component: null,
    };
  })();

  return (
    <div className={styles.drawerContainer}>
      <div style={{ minWidth: DEFAULT_SIDEBAR_WIDTH, height: "100%" }}>
        {selectedSidebar.component}
      </div>
    </div>
  );
}
